import React, {useCallback, useContext, useEffect, useState} from "react";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {useToasts} from 'react-toast-notifications';

import Container from "react-bootstrap/Container";

import ImageUploader from 'react-images-upload';
import axios from 'axios';

import ERC20TokenInfo from "../components/ERC20TokenInfo";

import Timestamp from "../components/utils/Timestamp";
import Math from "../components/utils/Math";
import Logger from "../components/utils/Logger";

import "../styles/Create.css";

import ERC20_ABI from "../ABI/ERC20";
import {ContractsContext, Web3Context} from "../contexts/Context";
import BN from "bn.js";
import Button from "react-bootstrap/Button";
import Image from '../components/farm/Image';
import NetworkUtil from "../components/utils/NetworkUtil";
import Transaction from "../components/common/Transaction";

const Create = () => {
  const PINATA_API = 'https://pinata-api.herokuapp.com/api';
  const ADDRESS_LENGTH = 42;
  const MB_LIMIT = 5;
  const MAX_FILE_SIZE = MB_LIMIT * 1024 * 1024;
  const FAUCET_AMOUNT = "5000000000000000000000";

  const [nftImage, setNftImage] = useState([]);
  const [tokenInfo, setTokenInfo] = useState({name: '', symbol: '', decimals: null, address: '', hasBonus: false});
  const [supply, setSupply] = useState(null);
  const [lockPeriod, setLockPeriod] = useState(null);
  const [minLockPeriod, setMinLockPeriod] = useState(null);
  const [fee, setFee] = useState(null);
  const [txHash, setTxHash] = useState(null);

  const web3Context = useContext(Web3Context);
  const contractsContext = useContext(ContractsContext);

  const {web3, activeAccount, networkId} = web3Context;
  const {contractNiftyEggsFarmer, contractNiftyEggsERC20, contractFaucet, balanceERC20, symbolERC20, decimalsERC20} = contractsContext;

  const {addToast} = useToasts();

  const showError = useCallback((content) => {
    addToast(content, {appearance: 'error'})
  }, [addToast]);

  const showSuccess = (content) => {
    addToast(content, {appearance: 'success'})
  };

  const showInfo = useCallback((content) => {
    addToast(content, {appearance: 'info'})
  }, [addToast]);

  const onAddressLostFocus = (e) => {
    changeAddress(e.target.value);
  };

  const changeAddress = (addressValue) => {
    if (addressValue === tokenInfo.address) return;
    if (addressValue.length === ADDRESS_LENGTH) {
      setTokenInfo({name: '', symbol: '', decimals: null, address: addressValue, hasBonus: false});
    } else {
      setTokenInfo({name: '', symbol: '', decimals: null, address: '', hasBonus: false});
    }
  };

  useEffect(() => {
    Logger.log("useEffect loadMinLockPeriod");

    const loadMinLockPeriod = async () => {
      if (!contractNiftyEggsFarmer) return;
      const minLockPeriodTimestamp = await contractNiftyEggsFarmer.methods.minLockPeriod().call();
      const minLockPeriodDateString = Timestamp.unixTimestampToDays(minLockPeriodTimestamp);
      console.log("min lock period", minLockPeriodDateString);
      setMinLockPeriod(minLockPeriodDateString);
    };

    loadMinLockPeriod();
  }, [contractNiftyEggsFarmer]);


  useEffect(() => {
    Logger.log("useEffect loadERC20");

    const loadERC20 = async () => {
      const address = tokenInfo.address;
      if (!address || address.length === 0) return;
      try {
        const contract = new web3.eth.Contract(ERC20_ABI, address);
        const name = await contract.methods.name().call();
        const symbol = await contract.methods.symbol().call();
        const decimals = await contract.methods.decimals().call();
        const rewardRate = await contractNiftyEggsFarmer.methods.rewardRates(address).call();
        const hasBonus = rewardRate && rewardRate > 0;
        setTokenInfo({name, symbol, decimals, address, hasBonus});
      } catch (e) {
        Logger.error(e);
        showError("Error while loading ERC20 token");
      }
    };

    loadERC20();
  }, [web3, contractNiftyEggsFarmer, tokenInfo.address, showError]);

  useEffect(() => {
    Logger.log("useEffect loadFee");

    const loadFee = async () => {
      if (supply && lockPeriod) {
        // convert days to seconds
        const seconds = Timestamp.daysToSeconds(lockPeriod);
        const feeWithDecimals = await contractNiftyEggsFarmer.methods.getCreateFee(supply, seconds).call();
        const feeNoDecimals = Math.removeDecimals(feeWithDecimals, decimalsERC20);

        setFee(feeNoDecimals.toString());
      }
    };

    loadFee();
  }, [web3, contractNiftyEggsFarmer, supply, lockPeriod, decimalsERC20]);

  useEffect(() => {
    Logger.log("useEffect showTx");

    const showTx = async () => {
      if (txHash) {
        showInfo
        (
          <span>Transaction hash:
            <Transaction txHash={txHash} format={false}/>
          </span>
        );
      }
    };

    showTx();
  }, [txHash, showInfo]);

  const faucetFunc = async () => {
    Logger.log("Faucet func");

    try {
      const result = await contractFaucet.methods.mint(FAUCET_AMOUNT).send({
        from: activeAccount
      }, (err, txHash) => setTxHash(txHash));

      Logger.log(result);
      if (result.status === true) {
        showSuccess("Success: you claimed 5000 free tokens!");
      } else {
        showError("Transaction failed!");
      }
    } catch (e) {
      Logger.error(e);
      showError("Error while claiming from faucet ", e);
    }
  };

  const createFunc = async (values) => {
    Logger.log("Create func");
    showInfo('Creating NFT. Please wait...');

    const name = values.name;
    const desc = values.description;
    const erc20Address = values.address;
    const amount = values.amount;
    const periodInDays = values.period;
    const supply = values.supply;
    const file = values.image[0];

    Logger.log(name, desc, erc20Address, amount, periodInDays, supply, file);

    if (!name || !desc || !erc20Address || !amount || !amount || !periodInDays || !supply || !file) {
      showError("Missing required fields!");
      return false;
    }
    if (!file.type.match(/image-*/)) {
      showError('Invalid file format');
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      showError("File too large (max allowed " + MB_LIMIT + "MB)");
      return false;
    }
    if (!tokenInfo.decimals) {
      showError('Invalid ERC20 token');
      return false;
    }

    try {
      // convert days to seconds
      const seconds = Timestamp.daysToSeconds(periodInDays);

      // add decimals to amount
      const amountWithDecimals = Math.addDecimals(amount, tokenInfo.decimals);

      // check fee
      const balanceBN = new BN(balanceERC20);
      const feeBN = new BN(fee);

      if (balanceBN.lt(feeBN)) {
        Logger.log("Insufficient token balance!");
        showError("Insufficient token balance!");
        return false;
      }

      let data = new FormData();
      data.append('file', file);

      const responsePinFile = await axios.post(PINATA_API + '/pinFile', data,
        {
          maxContentLength: 'Infinity', //this is needed to prevent axios from erroring out with large files
          headers: {
            'Content-Type': `multipart/form-data; boundary=${data._boundary}`
          }
        });

      const imgHash = responsePinFile.data;
      Logger.log("Image hash", imgHash);

      if (!imgHash || responsePinFile.status !== 200) {
        Logger.log("Failed to pin image");
        showError("Failed to pin the image");
        return false;
      }

      const JSONBody = {
        name: name,
        description: desc,
        image: 'https://ipfs.io/ipfs/' + imgHash,
        external_url: "https://niftyeggs.eth.link",
        attributes: []
      };

      const responsePinJSON = await axios.post(PINATA_API + '/pinJSON', JSONBody);

      const metadataHash = responsePinJSON.data;
      Logger.log("Metadata hash", metadataHash);

      if (!metadataHash || responsePinJSON.status !== 200) {
        Logger.log("Failed to pin JSON");
        showError("Failed to pin the metadata");
        return false;
      }

      const uri = "https://ipfs.io/ipfs/" + metadataHash;

      const contract = new web3.eth.Contract(ERC20_ABI, contractNiftyEggsERC20._address);
      const allowance = await contract.methods.allowance(activeAccount, contractNiftyEggsFarmer._address).call();
      Logger.log(`Account ${activeAccount} allowance of token ${contractNiftyEggsERC20._address} to ${contractNiftyEggsFarmer._address} is ${allowance}`);
      const allowanceBN = new BN(allowance);
      if (allowanceBN.lt(feeBN)) {
        Logger.log("Approving...");
        showInfo("First you need to approve tokens. This only needs to be done once for this token.");
        const UINT_MAX = "115792089237316195423570985008687907853269984665640564039457584007913129639935";
        const approved = await contract.methods.approve(contractNiftyEggsFarmer._address, UINT_MAX).send({
          from: activeAccount
        }, (err, txHash) => setTxHash(txHash));

        Logger.log("approved", approved);
        if (approved.status === true) {
          showInfo("Tokens approved. Now you need to confirm another transaction which creates the actual NFT reward.");
        } else {
          showError("Transaction failed");
          return false;
        }
      }

      const result = await contractNiftyEggsFarmer.methods
        .createNewFarm(erc20Address.toString(), supply.toString(), amountWithDecimals.toString(), seconds.toString(), uri)
        .send({
          from: activeAccount
        }, (err, txHash) => setTxHash(txHash));

      Logger.log(result);
      if (result.status === true) {
        showSuccess("Success: you created new NFT reward!");
      } else {
        showError("Transaction failed.");
      }

      return true;
    } catch (e) {
      Logger.error(e);
      showError("Error while creating NiftyEgg", e);
      return false;
    }
  };

  const [previewImage, setPreviewImage] = useState('');
  const reader = new FileReader();

  reader.addEventListener('load', (e) => {
    setPreviewImage(e.target.result);
  });

  const onImageChange = (img) => {
    setNftImage(img);

    if (img && img.length > 0) {
      try {
        reader.readAsDataURL(img[0]);
      } catch (e) {
        setPreviewImage('');
      }
    } else {
      setPreviewImage('');
    }
  };

  return (
    <div className="px-4 py-5 container-fluid">
      <div className="row no-gutters">
        <div className="col-12 col-md-10 offset-md-1 text-center">
          <h2>Create NFT Reward</h2>
          <div className="mb-2 text-warning">( Creation fee depends on the supply and lock period. )</div>
          <div className="mb-4 text-danger">( ERC20 tokens that have a transfer fee are not supported. Please do not use them! )</div>
          <Formik
            initialValues={{name: '', description: '', image: [], address: '', amount: '', period: '', supply: ''}}
            validate={values => {
              const errors = {};
              if (!values.name) {
                errors.name = 'Required';
              }
              if (!values.description) {
                errors.description = 'Required';
              }
              if (!values.address) {
                errors.address = 'Required';
              } else if (values.address.length === 42) {
                changeAddress(values.address);
              } else {
                changeAddress('');
              }
              // else if (values.address.length !== 42) {
              //   errors.address = 'Length mus be 42';
              // } 
              if (!values.amount) {
                errors.amount = 'Required';
              } else if (values.amount < 1) {
                errors.amount = 'Min. 1';
              }
              if (!values.supply && values.supply !== 0) {
                errors.supply = 'Required';
              } else if (values.supply < 1) {
                errors.supply = 'Min. 1';
              }
              if (!values.period && values.period !== 0) {
                errors.period = 'Required';
              } else if (values.period < 1) {
                errors.period = 'Min. 1';
              }
              values.image = nftImage;
              if (!nftImage || nftImage.length === 0) {
                errors.image = 'Required';
              }
              return errors;
            }}
            onSubmit={async (values, {setSubmitting, resetForm}) => {
              const success = await createFunc(values);
              if (success) resetForm();
            }}
          >
            {({
                values,
                errors,
                touched,
                dirty,
                handleChange,
                handleBlur,
                handleSubmit,
                handleReset,
                isSubmitting,
              }) => (
              <Form onSubmit={handleSubmit} onReset={handleReset}>

                <Container fluid>

                  {/* Name */}
                  <div className="form-group row align-items-center text-left text-lg-right">
                    <label htmlFor="name" className="col-12 col-lg-2 offset-sm-1 col-form-label"
                           title="NFT name">Name<sup>*</sup></label>
                    <div className="col-12 col-sm-10 col-lg-6 offset-sm-1 offset-lg-0">
                      <Field type="text" name="name" placeholder="NFT name" className="form-control" maxLength="75"/>
                    </div>
                    <ErrorMessage name="name" component="div" className="invalid-value"/>
                  </div>

                  {/* Description */}
                  <div className="form-group row align-items-center text-left text-lg-right">
                    <label htmlFor="description" className="col-12 col-lg-2 offset-sm-1 col-form-label"
                           title="NFT description">Description<sup>*</sup></label>
                    <div className="col-12 col-sm-10 col-lg-6 offset-sm-1 offset-lg-0">
                      <Field component="textarea" rows="3" name="description" placeholder="NFT description"
                             maxLength="200" className="form-control" style={{resize: "none"}}
                      />
                    </div>
                    <ErrorMessage name="description" component="div" className="invalid-value"/>
                  </div>

                  {/* Image */}
                  <div className="form-group row align-items-center text-left text-lg-right">
                    <label htmlFor="image" className="col-12 col-lg-2 offset-sm-1 col-form-label"
                           title="NFT image">Image<sup>*</sup></label>
                    <div className="col-12 col-sm-10 col-lg-6 offset-sm-1 offset-lg-0">
                      <ImageUploader
                        name="image"
                        withIcon={true}
                        buttonText='Choose image'
                        onChange={onImageChange}
                        imgExtension={['.jpg', '.gif', '.png', '.jpeg']}
                        accept="image/jpg, image/gif, image/png, image/jpeg"
                        maxFileSize={MAX_FILE_SIZE}
                        withPreview={true}
                        singleImage={true}
                      />
                    </div>
                    <ErrorMessage name="image" component="div" className="invalid-value"/>
                  </div>

                  {/* Supply */}
                  <div className="form-group row align-items-center text-left text-lg-right">
                    <label htmlFor="supply" className="col-12 col-lg-2 offset-sm-1 col-form-label"
                           title="Maximum number of this NFT that will be available">Supply<sup>*</sup></label>
                    <div className="col-12 col-sm-10 col-lg-6 offset-sm-1 offset-lg-0">
                      <Field type="number" name="supply" placeholder="e.g. 100" className="form-control"
                             onBlur={e => {
                               handleBlur(e); // Built-in handler
                               setSupply(e.target.value);
                             }}/>
                    </div>
                    <ErrorMessage name="supply" component="div" className="invalid-value"/>
                  </div>

                  {/* Address */}
                  <div className="form-group row align-items-center text-left text-lg-right">
                    <label htmlFor="address" className="col-12 col-lg-2 offset-sm-1 col-form-label"
                           title="ERC20 token address (42 symbols)">ERC20
                      Token Address<sup>*</sup></label>
                    <div className="col-12 col-sm-10 col-lg-6 offset-sm-1 offset-lg-0">
                      <Field type="text" name="address" placeholder="0x123456..." className="form-control"
                             maxLength={ADDRESS_LENGTH}
                             onBlur={e => {
                               handleBlur(e); // Built-in handler
                               onAddressLostFocus(e);
                             }}
                      />
                    </div>
                    <ErrorMessage name="address" component="div" className="invalid-value"/>
                  </div>

                  {/* Token Preview */}
                  <div className={tokenInfo.name === '' ? 'd-none' : ''}>
                    <div className="form-group row align-items-center text-center">
                      <div className="col-12 align-items-center d-flex justify-content-center">
                        <ERC20TokenInfo name={tokenInfo.name} symbol={tokenInfo.symbol} address={tokenInfo.address}/>
                      </div>
                      <div className="col-12 align-items-center d-flex justify-content-center">
                        <div className={tokenInfo.hasBonus ? 'text-warning' : 'd-none'}>
                          This token is included in the bonus reward list!
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="form-group row align-items-center text-left text-lg-right">
                    <label htmlFor="amount" className="col-12 col-lg-2 offset-sm-1 col-form-label"
                           title="Amount of tokens one will have to lock to get this NFT">Lock
                      amount<sup>*</sup></label>
                    <div className="col-12 col-sm-10 col-lg-6 offset-sm-1 offset-lg-0">
                      <Field type="number" name="amount" placeholder="e.g. 1000" className="form-control"/>
                    </div>
                    <ErrorMessage name="amount" component="div" className="invalid-value"/>
                  </div>

                  {/* Period */}
                  <div className="form-group row align-items-center text-left text-lg-right">
                    <label htmlFor="period" className="col-12 col-lg-2 offset-sm-1 col-form-label"
                           title="NFT lock period (in days)">Lock period in
                      days<sup>*</sup>(min. {minLockPeriod})</label>
                    <div className="col-12 col-sm-10 col-lg-6 offset-sm-1 offset-lg-0">
                      <Field type="number" name="period" placeholder="e.g. 30" className="form-control"
                             onBlur={e => {
                               handleBlur(e); // Built-in handler
                               setLockPeriod(e.target.value);
                             }}/>
                    </div>
                    <ErrorMessage name="period" component="div" className="invalid-value"/>
                  </div>

                  {/* Fee */}
                  {
                    fee ?
                      <div className="row align-items-center text-center">
                        <div className="col-12 align-items-center d-flex justify-content-center">
                          <h6>Total cost: <span className="text-warning">{fee > 0 ? fee + ' ' + symbolERC20 : 'FREE'}</span></h6>
                        </div>
                      </div>
                      :
                      null
                  }

                  {/* Buttons */}
                  <div className="form-group row align-items-center text-center">
                    <div className="col-12">
                      <button type="submit" disabled={isSubmitting} className="btn btn-primary mr-3 px-5 py-3">
                        Submit
                      </button>
                      <button type="button" className="btn btn-outline-secondary px-5 py-3 my-0 mr-3"
                              onClick={handleReset}
                              disabled={!dirty || isSubmitting}>
                        Reset
                      </button>
                    </div>
                  </div>

                  <div className={previewImage ? '' : 'd-none'}>
                    <Image id="imgId" image={previewImage} nftName="Preview"/>
                  </div>

                </Container>

              </Form>
            )}
          </Formik>
          {
            NetworkUtil.isTestnet(networkId) ?
              <div>
                <div className="mt-4">
                  Don't have any {symbolERC20} tokens to pay for the fee? Claim 5000 test tokens from the faucet:
                </div>
                <Button className="btn-lg btn-scale" variant="secondary"
                        onClick={() => faucetFunc()}>Claim!</Button>
              </div>
              :
              null
          }
        </div>
      </div>
    </div>
  )
};

export default Create;